import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import {
  BookOpen, CalendarDays, DollarSign, Sun, CalendarClock,
  Gauge, FileEdit, History, ArrowRight, TrendingUp, Clock,
  Sparkles, PuzzleIcon, BarChart3, LayoutDashboard, BadgeInfo, Target, Scale, MoonStar, Telescope,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

interface Topic {
  slug: string
  title: string
}

interface HelpIndexProps extends Pick<SharedProps, 'currentUser' | 'properties'> {
  topics: Topic[]
}

// `title` overrides the slug-derived card heading — used where the FEATURE has a name of its own
// that the slug cannot spell (Last-Minute Clearance). Everything else keeps the titleized slug.
const TOPIC_META: Record<string, { title?: string; description: string; icon: React.ElementType; color: string; bg: string; isNew?: boolean; isWip?: boolean }> = {
  'how-it-works': {
    description: 'Understand the 13-stage rate pipeline, compute cycle, and how rates flow from your rules to your PMS.',
    icon: BookOpen,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  'rate-calendar': {
    description: 'Your daily command center — read the rate grid, occupancy tiers, hover breakdowns, and day detail modal.',
    icon: CalendarDays,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
  'base-rates': {
    description: 'Your pricing anchor. Set base rates per room type — every other adjustment builds on this.',
    icon: DollarSign,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  seasons: {
    description: 'Capture predictable yearly demand patterns with date-based percentage modifiers.',
    icon: Sun,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  'day-of-week': {
    description: 'Price weekly demand rhythms — weekend premiums, midweek discounts, and everything between.',
    icon: CalendarClock,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
  },
  'demand-response': {
    description: 'How rates respond automatically to booking pace and occupancy. Choose your sensitivity level.',
    icon: TrendingUp,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
  },
  'lead-time': {
    description: 'How the booking window shapes demand sensitivity — closer dates react more strongly to pace signals.',
    icon: Clock,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
  },
  events: {
    description: 'Capture demand spikes from local events that your booking history can\'t predict.',
    icon: Sparkles,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
  },
  'gap-filling': {
    description: 'Turn hard-to-sell single-night gaps between bookings into revenue.',
    icon: PuzzleIcon,
    color: 'text-pink-600',
    bg: 'bg-pink-50',
  },
  'market-position': {
    description: 'Price relative to your competitive set — boost when you\'re cheaper than the market, soften when you\'re pricier. Powered by the Market Intelligence feed (rolling out).',
    icon: Scale,
    color: 'text-fuchsia-600',
    bg: 'bg-fuchsia-50',
    isWip: true,
  },
  clearance: {
    title: 'Last-Minute Clearance',
    description: 'Sell the last few rooms on the night. Close to arrival, prices step down on a schedule you can read in advance — and go back to normal once the rooms are gone or the night stops heading for empty.',
    icon: MoonStar,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    isNew: true,
  },
  'price-guardrails': {
    description: 'Your safety net — floor and ceiling limits that keep rates within acceptable bounds.',
    icon: Gauge,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
  },
  overrides: {
    description: 'Take manual control when you know something the system doesn\'t. Set rates, restrictions, and stop-sells.',
    icon: FileEdit,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
  },
  'occupancy-metrics': {
    description: 'Understand occupancy, ADR, and booking pickup — and what they tell you about your pricing health.',
    icon: BarChart3,
    color: 'text-sky-600',
    bg: 'bg-sky-50',
  },
  dashboard: {
    description: 'Your at-a-glance overview of property health, sync status, and pipeline activity.',
    icon: LayoutDashboard,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
  },
  'market-intelligence': {
    description: "See your price against your competitors' list prices on Booking.com, night by night: who is sold out, who moved, and which nights are worth a look.",
    icon: Telescope,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    isNew: true,
  },
  history: {
    description: 'Every price the engine moved and every rule someone changed: who changed it, when, what it was before, and how to undo it.',
    icon: History,
    color: 'text-stone-600',
    bg: 'bg-stone-50',
    isNew: true,
  },
  'targets': {
    description: 'Set monthly occupancy, ADR, and RevPAR targets. The engine steers rates toward your commitment — pushing harder when behind, easing off when ahead.',
    icon: Target,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    isNew: true,
  },
}

const SECTIONS: Array<{ title: string; slugs: string[] }> = [
  {
    title: 'Getting Started',
    slugs: ['how-it-works'],
  },
  {
    title: 'Pricing Rules',
    slugs: ['rate-calendar', 'base-rates', 'seasons', 'day-of-week', 'demand-response', 'lead-time', 'events', 'gap-filling', 'clearance', 'market-position', 'price-guardrails', 'overrides'],
  },
  {
    title: 'Monitoring',
    slugs: ['occupancy-metrics', 'dashboard', 'market-intelligence', 'history'],
  },
  {
    title: 'Target Setting',
    slugs: ['targets'],
  },
]

function TopicCard({ slug, index }: { slug: string; index: number }) {
  const meta = TOPIC_META[slug]
  if (!meta) return null
  const Icon = meta.icon

  return (
    <Link
      href={`/guides/${slug}`}
      className="animate-slide-in group flex flex-col items-start gap-3 rounded-xl border border-border/60 bg-white p-5 text-left shadow-sm transition-all hover:shadow-md hover:border-primary/30"
      style={{ animationDelay: `${80 + index * 40}ms` }}
    >
      <div className="flex w-full items-start justify-between">
        <div className={cn(
          'flex h-10 w-10 items-center justify-center rounded-xl transition-transform group-hover:scale-110',
          meta.bg,
          meta.color,
        )}>
          <Icon className="h-5 w-5" />
        </div>
        {meta.isNew && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
            New
          </span>
        )}
        {meta.isWip && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
            Preview
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
          {meta.title ?? slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {meta.description}
        </p>
      </div>
      <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Read guide <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  )
}

export default function HelpIndex({ currentUser, properties, topics }: HelpIndexProps) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  // Build ordered topic list from sections, filtered to only topics the backend sent
  const backendSlugs = new Set(topics.map(t => t.slug))
  let globalIndex = 0

  return (
    <PortfolioLayout title="Help & Guides" currentUser={currentUser} properties={properties}>
      <div className="space-y-8">
        <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Help & Guides</h1>
            <BadgeInfo className="h-4 w-4 text-muted-foreground/40" />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Learn how to use {brand} to manage your property rates effectively. Each guide includes interactive tools to help you understand the concepts.
          </p>
        </div>

        {SECTIONS.map((section) => {
          const sectionSlugs = section.slugs.filter(s => backendSlugs.has(s))
          if (sectionSlugs.length === 0) return null

          return (
            <div key={section.title}>
              <div className="animate-slide-in mb-3" style={{ animationDelay: `${60 + globalIndex * 40}ms` }}>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {sectionSlugs.map((slug) => {
                  const card = <TopicCard key={slug} slug={slug} index={globalIndex} />
                  globalIndex++
                  return card
                })}
              </div>
            </div>
          )
        })}
      </div>
    </PortfolioLayout>
  )
}
